// ROLE-COMPAT-001 — UI mirror of backend/services/role_compatibility.py (the API stays authoritative).
export const DEFAULT_COMPAT = {
  tax_roles: ['processor'],
  title_roles: ['search_processor', 'typing_processor'],
  qc_role: 'qc_specialist',
  dual_role_primaries: ['processor', 'search_processor', 'typing_processor', 'qc_specialist'],
  // OD-DUAL-ROLE-001: non-operational primaries that may hold an explicit additional processing role
  extended_primaries: { order_desk: ['search_processor'] },
  legacy_aliases: { title_search_processor: 'search_processor', tax_search_processor: 'processor' },
  labels: {
    processor: 'Tax Processor', search_processor: 'Search Processor', typing_processor: 'Typing Processor',
    qc_specialist: 'QC Specialist', title_search_processor: 'Title Search Processor', tax_search_processor: 'Tax Search Processor',
    order_desk: 'Order Desk',
  },
};

export const canonicalRole = (key, cfg = DEFAULT_COMPAT) => {
  const k = String(key || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return cfg.legacy_aliases?.[k] || k;
};

export const isLegacyRole = (key, cfg = DEFAULT_COMPAT) => !!cfg.legacy_aliases?.[String(key || '').toLowerCase()];

export const roleLabel = (key, cfg = DEFAULT_COMPAT) => cfg.labels?.[key] || String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const workstreamOf = (key, cfg = DEFAULT_COMPAT) => {
  const k = canonicalRole(key, cfg);
  if (cfg.tax_roles.includes(k)) return 'tax';
  if (cfg.title_roles.includes(k)) return 'title';
  return null;
};

export const extendedAdditional = (primary, cfg = DEFAULT_COMPAT) => (cfg.extended_primaries || {})[canonicalRole(primary, cfg)] || null;

export const canHoldAdditional = (primary, cfg = DEFAULT_COMPAT) =>
  cfg.dual_role_primaries.includes(canonicalRole(primary, cfg)) || !!extendedAdditional(primary, cfg);

// Candidates for the checkbox group given primary + QC scope (ignores current selections).
export const offerableAdditional = (primary, scope, cfg = DEFAULT_COMPAT) => {
  const p = canonicalRole(primary, cfg);
  const ext = extendedAdditional(p, cfg);
  if (ext) return [...ext];
  if (!cfg.dual_role_primaries.includes(p)) return [];
  const scopeSet = new Set(scope || []);
  return cfg.dual_role_primaries.filter((cand) => {
    if (cand === p) return false;
    if (cand === cfg.qc_role) return true;
    const ws = workstreamOf(cand, cfg);
    if (p === cfg.qc_role) return scopeSet.size > 0 && scopeSet.has(ws);
    return ws === workstreamOf(p, cfg);
  });
};

// Why `cand` cannot be added on top of primary + selected (null = allowed).
export const blockReason = (cand, primary, selected, cfg = DEFAULT_COMPAT) => {
  const ws = workstreamOf(cand, cfg);
  if (!ws) return null;
  const ext = extendedAdditional(primary, cfg);
  if (ext) return ext.includes(canonicalRole(cand, cfg)) ? null : `${roleLabel(primary, cfg)} may only hold ${ext.map((r) => roleLabel(r, cfg)).join(', ')} as an additional role.`;
  const held = [primary, ...(selected || [])].filter((r) => canonicalRole(r, cfg) !== canonicalRole(cand, cfg));
  const other = held.find((r) => workstreamOf(r, cfg) && workstreamOf(r, cfg) !== ws);
  return other ? `${roleLabel(cand, cfg)} cannot be combined with ${roleLabel(canonicalRole(other, cfg), cfg)} — Tax and Title Search / Typing are separate workstreams.` : null;
};

// Split current additional roles into compatible vs removed ({ role, reason }) for a primary + scope.
export const reconcileAdditional = (primary, additional, scope, cfg = DEFAULT_COMPAT) => {
  const offer = offerableAdditional(primary, scope, cfg);
  const kept = [];
  const removed = [];
  (additional || []).forEach((r) => {
    const c = canonicalRole(r, cfg);
    if (c === canonicalRole(primary, cfg)) return;
    if (!offer.includes(c)) {
      const p = canonicalRole(primary, cfg);
      const why = !canHoldAdditional(p, cfg)
        ? `${roleLabel(p, cfg)} must remain single-role.`
        : extendedAdditional(p, cfg)
          ? `${roleLabel(p, cfg)} may only hold ${extendedAdditional(p, cfg).map((r) => roleLabel(r, cfg)).join(', ')} as an additional role.`
        : p === cfg.qc_role
          ? (scope && scope.length ? `QC Product Scope (${scope.map((s) => s[0].toUpperCase() + s.slice(1)).join(' + ')}) does not cover ${roleLabel(c, cfg)}.` : 'QC Product Scope is not set — set it before adding processing roles.')
          : `${roleLabel(c, cfg)} is not compatible with ${roleLabel(p, cfg)} — separate workstreams.`;
      removed.push({ role: c, reason: why });
      return;
    }
    const reason = blockReason(c, primary, kept, cfg);
    if (reason) removed.push({ role: c, reason });
    else kept.push(c);
  });
  return { kept, removed };
};
